import { faBookBookmark, faComments, faConciergeBell, faFile, faList, faMagnifyingGlassChart, faSchool, faUser, faUsers, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { ManagementUser } from "src/app/core/models/management-user.model";
import { SessionStorageService } from "src/app/core/services/session-storage.service";

export interface SidebarSection {
  id: string;
  label: string,
  color: 'green' | 'blue' | 'orange';
  categories: SidebarCategory[];
}

export interface SidebarCategory {
  id: string;
  name: string;
  links?: SidebarLink[];
  // groups come after the links and have their own labels
  groups?: SidebarGroup[];
}

export interface SidebarGroup {
  name: string;
  links: SidebarLink[];
}

export interface SidebarLink {
  name: string;
  route: string;
  id: string;
  exactRoute?: true;
  // condition === undefined is treated as being shown
  condition?: boolean;
}

export function getUserNav(
  u: ManagementUser,
  isSSO: boolean,
  isDemoModeEnabled: boolean,
  showManageTeacherSubscriptions: boolean,
) {
  const isSchoolDistrictAdminUser = u.isSchoolUser() || u.isDistrictUser() || u.isSchoolAdmin();
  const showReports = isSchoolDistrictAdminUser || u.isFILUser();

  const reportsScope = (u.isDistrictUser() || u.isFILUser()) ? 'District' : 'School';

  const nav: SidebarSection[] = [
    {
      id: 'internal',
      label: 'internal links',
      color: 'orange',
      categories: [
        {
          id: 'partner-success',
          name: 'Partner Success',
          links: [
            {
              name: 'Dashboard',
              id: 'partner-success-dashboard',
              route: '/partner-success/dashboard',
              condition: u.isFoundationsReportsUser() || u.isPartnerSuccessUser()
            }
          ]
        }
      ]
    },
    {
      id: 'disctrict-section',
      label: 'system links',
      color: 'green',
      categories: [
        {
          id: 'school-admin',
          name: 'School Admin',
          links: [
            {
              name: 'User List',
              id: 'user-list',
              route: '/users/list',
              condition: u.isSchoolAdmin()
            },
          ],
        },
        {
          name: `${reportsScope} Reports`,
          id: `school-district-reports`,
          links: [
            {
              name: 'School Dashboard',
              id: 'schoolDashboardAC',
              route: '/reports/school/dashboard',
              condition: (u.isSchoolUser() || u.isSchoolAdmin()) && u.fullProductEnabled,
            },
            {
              // only SCHOOL_REPORTS users are pointed to the new dashboard
              name: `District Dashboard`,
              id: 'districtDashboardAC',
              route: '/reports/district/dashboard',
              condition: (u.isDistrictUser() || u.isFILUser()) && u.fullProductEnabled,
            },
          ],
          groups: [
            {
              name: 'Assessments',
              links: [
                {
                  name: 'Screener Summary',
                  id: 'district-summary',
                  route: '/reports/district/screener',
                  condition: showReports,
                },
                {
                  name: 'Diagnostic Summary',
                  id: 'district-diagnostic',
                  route: '/reports/district/diagnostic',
                  condition: showReports && (u.diagnosticProductEnabled || u.fullProductEnabled),
                },
              ]
            },
            {
              name: 'Progress Monitoring',
              links: [
                {
                  name: 'Usage Detail',
                  id: 'usage-detail',
                  route: '/reports/district/usage',
                  condition: showReports && u.fullProductEnabled,
                },
              ]
            },
            {
              name: 'Enrollment',
              links: [
                {
                  name: 'Student List',
                  id: 'student-list',
                  route: '/students/list',
                  condition: showReports,
                },
              ]
            }
          ]
        },
      ]
    },
    {
      id: 'school-section',
      label: 'school links',
      color: 'blue',
      categories: [
        {
          id: 'getting-started',
          name: 'Getting Started',
          links: [
            {
              name: 'Checklist',
              id: 'getting-started-checklist',
              route: '/getting-started',
              condition: !u.fullProductEnabled && !u.diagnosticProductEnabled && (u.isTeacherUser() || u.isTeacherSchoolAdmin()),
            },
          ],
        },
        {
          id: 'enroll',
          name: 'Enroll',
          links: [
            {
              name: 'Batch Enrollment',
              id: 'batchEnrollment',
              route: '/batch-enrollment',
              condition:
                !isSSO
                && u.isSchoolAdmin()
                || u.isCustomerServiceUser(),
            },
            {
              name: 'Student List',
              id: 'studentListNavLink',
              route: '/students',
              exactRoute: true,
              condition: u.isTeacherUser() || u.isSchoolAdmin(),
            },
            {
              name: 'Co-Teachers',
              id: 'subscriptionsNavLink',
              route: '/reports/system/co-access',
              condition: !u.isFreeUser() && (u.isTeacherUser() || u.isSchoolAdmin())
            },
          ],
          groups: [
            {
              name: 'Manage Subscriptions',
              links: [
                {
                  name: 'Student Subscriptions',
                  id: 'studentSubscriptionsNavLink',
                  route: '/students/manage-subscriptions',
                  condition: u.isTeacherUser() || u.isSchoolAdmin() || u.isCustomerServiceUser(),
                },
                {
                  name: 'Teacher Subscriptions',
                  id: 'teacherSubscriptionsNavLink',
                  route: '/teachers/manage-subscriptions',
                  condition: showManageTeacherSubscriptions && (u.isSchoolAdmin() || u.isCustomerServiceUser()),
                },
              ]
            }
          ]
        },
        {
          name: 'Notifications',
          id: 'notifications',
          links: [
            {
              name: 'Recent',
              id: 'notifications-recent',
              route: '/notifications/recent',
              condition: u.isTeacherUser(),
            },
            {
              name: 'History',
              id: 'notifications-history',
              route: '/notifications/history',
              condition: u.isTeacherUser(),
              // disabled: // TODO: History should be disabled if there are no notifications
            },
          ],
        },
        {
          name: 'Reports',
          id: 'class-reports',
          links: [
            {
              name: 'Teacher Dashboard',
              id: 'teacher-dashboard',
              route: '/dashboard/system',
              condition: u.fullProductEnabled,
            },
          ],
          groups: [
            {
              name: 'Assessments',
              links: [
                {
                  name: 'Screener Results',
                  id: 'screener-results',
                  route: 'reports/screener',
                },
                {
                  name: 'Diagnostic Results',
                  id: 'diagnostic-results',
                  route: '/reports/system/diagnostic',
                  condition: u.fullProductEnabled,
                },
                {
                  name: 'Standalone Diagnostic',
                  id: 'standalone-diagnostic',
                  route: '/reports/diagnostic/diagnostic',
                  condition: u.diagnosticProductEnabled && !isDemoModeEnabled,
                },
              ]
            },
            {
              name: 'Progress Monitoring',
              links: [
                {
                  name: 'Group Summary',
                  id: 'group-summary',
                  route: '/reports/system/summary/group',
                  condition: u.fullProductEnabled,
                },
                {
                  name: 'Objective Pre/Post',
                  id: 'objective-pre-post',
                  route: '/reports/system/objective',
                  condition: u.fullProductEnabled,
                },
                {
                  name: 'Unit Detail',
                  id: 'unit-detail',
                  route: '/reports/system/unit-detail',
                  condition: u.fullProductEnabled,
                },
                {
                  name: 'Session Summary',
                  id: 'session-summary',
                  route: '/reports/system/summary/session',
                  condition: u.fullProductEnabled,
                },
                {
                  name: 'Student Summary',
                  id: 'student-summary',
                  route: '/reports/system/summary/student',
                  condition: u.fullProductEnabled,
                },
              ]
            }
          ]
        },
        {
          name: 'Small Group Instruction',
          id: 'small-group',
          links: [
            {
              name: 'Create Lesson Plan',
              id: 'create-lession-plan',
              route: '/small-group/generator',
              condition: u.fullProductEnabled,
            },
            {
              name: 'Demo Videos',
              id: 'demo-videos',
              route: '/small-group/system/all/model-lessons',
              condition: u.fullProductEnabled,
            },
            {
              name: 'Connected Texts',
              id: 'connected-texts',
              route: '/small-group/system/all/Poems & Passages',
              condition: u.fullProductEnabled,
            },
          ]
        },
        {
          name: 'Student Engagement',
          id: 'student-engagement',
          links: [
            {
              name: 'Student Goal Setting',
              id: 'student-goal-setting',
              route: '/student-engagement/system/all/Student Goal Setting',
              condition: u.fullProductEnabled,
            },
            {
              name: 'Character Trackers',
              id: 'character-trackers',
              route: '/student-engagement/system/all/Character Trackers',
              condition: u.fullProductEnabled,
            },
          ]
        },
        {
          name: 'Knowledge Base',
          id: 'resources',
          links: [
            {
              name: 'Screener',
              id: 'screener-knowledge-base',
              route: '/resources/screener',
            },
            {
              name: 'User Guides',
              id: 'user-guides',
              route: '/resources/system/all/User Guides',
            },
            {
              name: 'Videos',
              id: 'knowledge-base-videos',
              route: '/resources/videos',
            }
          ]
        },
        {
          name: 'Files',
          id: 'files',
          links: [
            {
              name: 'Secure Data Sharing',
              id: 'secure-data-sharing',
              route: '/secure-data-sharing',
              condition: !u.isFoundationsReportsUser(),
            },
          ]
        },
        {
          name: 'Support',
          id: 'support',
          links: [
            {
              name: 'Contact',
              id: 'contact',
              route: '/support/contact',
              condition: !u.isFILUser(),
            },
            {
              name: 'Surveys',
              id: 'surveys',
              route: '/survey',
              condition: !u.isTeacherReviewUser() && !u.isFILUser(),
            },
          ]
        }
      ]
    }
  ]

  return pruneNavTree(nav);
}

// Destructively prune sections array to only those links the user has access to
function pruneNavTree(nav: SidebarSection[]) {
  return nav.filter(section => {
    section.categories = section.categories.filter(section => {
      section.links = section.links?.filter(l => l.condition !== false);
      section.groups = section.groups?.filter(group => {
        group.links = group.links.filter(l => l.condition !== false);
        return group.links.length > 0;
      });

      return (section.links ?? []).length > 0 || (section.groups ?? []).length > 0;
    });
    return section.categories.length > 0;
  })
}
